/**
 * The tool a paired session uses to do nothing (RFC 0301 §3b).
 *
 * A turn with nothing to add used to have one move, a message, so it wrote one.
 * `pass` makes silence a choice on the record: it appends `mod.passed` naming
 * the turn's run, and the firing fold reads that as a turn that looked and let
 * it be.
 *
 * NOT A WRITE TOOL. It touches only this session's own log, so it stays in the
 * tool set when writes are off, which is exactly when a quiet turn matters.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../log/branch-log'
import { markCommentRead } from '../model/comments/mutations'
import { projectSessionActiveRuns } from '../model/session/reads'
import type { ToolDef } from './project-tools'
export function pairingTools(
  repo: Repo,
  sessionId: string | undefined,
  /** Where a passed-on comment lives, so the thread can say it was read. */
  projectId?: string,
): Record<string, ToolDef> {
  if (!sessionId) return {}
  return {
    pass: {
      description:
        'Do nothing this turn, on the record. Call it when you were started ' +
        'by a signal (a comment or an edit on a document you are ' +
        'paired to) and have nothing worth saying or doing. After calling ' +
        'it, end the turn WITHOUT writing any text: the point is that the ' +
        'thread stays as it was. Give a one-line note on why, for the record. ' +
        'When a comment woke you, pass its id as commentId, so its thread ' +
        'says you read it.',
      inputSchema: {
        type: 'object',
        properties: {
          note: {
            type: 'string',
            description: 'One line on why nothing was needed.',
          },
          commentId: {
            type: 'string',
            description:
              'The comment that woke you, when one did: its thread then shows "read".',
          },
        },
      },
      execute: async (args) => {
        const handle = await repo.find(sessionId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        const active = projectSessionActiveRuns(handle.doc())
        // The claimed run is the one calling this. A queued one behind it has
        // not started and cannot have decided anything.
        const run = active.find((r) => r.claimed) ?? active[0]
        if (!run) return 'Nothing to pass: this session has no turn running.'
        const note = args.note != null ? String(args.note).slice(0, 280) : ''
        new BranchLog(handle as never).appendPassed({
          runId: run.runId,
          ...(note ? { note } : {}),
        })
        const commentId = args.commentId != null ? String(args.commentId) : ''
        if (commentId && projectId) {
          await markCommentRead(repo, projectId, commentId, sessionId)
        }
        return 'Passed. End the turn now, and write nothing.'
      },
    },
  }
}
